import { Component, OnInit, ViewChild } from '@angular/core';
import { EMCType } from '../../../enums/emc-type';
import { SortType } from '../../../models/sortType.model';
import { ChecklistItem } from '../../../models/checklist-item.model';
import { TableListPage } from '../../../models/list-page.model';
import { SubstationTemplateService } from '../../../services/substation-template.service';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute } from '@angular/router';
import { SectionDefinitionEntityType } from '../../../models/section-definition.model';
import { EntityModelCustomizationService } from '../../../services/entity-model-customization.service';
import { Purpose } from '../../../enums/purpose';
import { EmcOptions } from '../../../models/entity-model-customization.model';
import { Filter } from '../../../models/dynamic-filter.model';
import { EmcSearchOutput } from '../../../models/emc-search.model';
import { DynamicCardsComponent } from '../../entity-model-customization/dynamic-cards/dynamic-cards.component';
import { UtilitySharedService } from '../../../services/shared/utility-shared.service';
import { TaggedSubstationService } from '../../../services/tagged-substation.service';
import { PermissionsService } from '../../../services/permissions.service';

@Component({
  selector: 'lib-cim-substation-list',
  templateUrl: './cim-substation-list.component.html',
  styleUrl: './cim-substation-list.component.scss',
  standalone: false,
})
export class CimSubstationListComponent implements OnInit {
  @ViewChild('dynamicCards') dynamicCards!: DynamicCardsComponent;

  items: any = [];
  title: string = '';
  substationTemplateId: number = 0;
  utilityId: number = 0;

  entityType: EMCType =
    EMCType.SubstationTemplate;

  mapSortBy: Record<number, string> = {
    [SortType.NEWEST]: 'Newest',
    [SortType.ID_ASC]: 'ID',
    [SortType.ID_DESC]: 'ID',
    [SortType.SLRN_ASC]: 'SLRN',
    [SortType.SLRN_DESC]: 'SLRN',
  };

  statusLabelMap: Record<number, string> = { 0: 'Pending', 1: 'Complete', 3: 'Assigned', 4: 'Rejected' };
  gisReviewStatusLabelMap: Record<number, string> = { 1: 'Not Validated', 2: 'Validated', 3: 'Auto Validated' };
  gisUpdateStatusLabelMap: Record<number, string> = { 1: 'Approved', 2: 'Rejected' };

  mapFilterValues: Record<string, string> = {
    'First Captured User': 'COMMON.USER.TYPE.FIRST_CAPTURED',
    'Last Captured User': 'COMMON.USER.TYPE.LAST_CAPTURED',
    'Reassigned By': 'COMMON.USER.TYPE.REASSIGNED',
    'Validated By': 'COMMON.USER.TYPE.VALIDATED',
    'Audited By': 'COMMON.USER.TYPE.AUDITED',
    Pending: 'COMMON.ASSET_STATUS.PENDING',
    Complete: 'COMMON.ASSET_STATUS.COMPLETE',
    Rejected: 'COMMON.ASSET_STATUS.REJECTED',
    Assigned: 'COMMON.ASSET_STATUS.ASSIGNED',
    'Last Update Time': 'COMMON.DATE_TYPE.LAST_UPDATE',
    'Tagged Date': 'COMMON.DATE_TYPE.TAGGED',
    'Validated Date': 'COMMON.DATE_TYPE.VALIDATED',
    'Audited Date': 'COMMON.MBC_DETAILS.AUDITED_DATE',
    ReviewDate: 'COMMON.MBC_DETAILS.REVIEW_DATE',
    'GIS Review Date': 'COMMON.GIS_REVIEW_DATE',
  };

  mapFilterLabels: Record<string, string> = {
    Search: 'COMMON.SEARCH',
    'Filter By': 'COMMON.FILTER_BY',
    'User Type': 'COMMON.USER.TYPE.TITLE',
    Type: 'COMMON.TYPE',
    'Validation Status': 'COMMON.VALIDATION_STATUS',
    'Review Status': 'COMMON.REVIEW_STATUS',
    'Audit Status': 'COMMON.AUDIT_STATUS',
    'GIS Review Status': 'COMMON.GIS_REVIEW_STATUS',
    'GIS Update Status': 'COMMON.GIS_UPDATE_STATUS',
    'Date From': 'COMMON.DATE_FROM',
    'Date To': 'COMMON.DATE_TO',
    'Date Type': 'COMMON.DATE_TYPE.TITLE',
    Area: 'COMMON.UT.SINGLE',
    'First Captured User': 'COMMON.USER.TYPE.FIRST_CAPTURED',
    'Last Captured User': 'COMMON.USER.TYPE.LAST_CAPTURED',
    'Reassigned By': 'COMMON.USER.TYPE.REASSIGNED',
    'Validated By': 'COMMON.USER.TYPE.VALIDATED',
    'Audited By': 'COMMON.USER.TYPE.AUDITED',
    'Reviewed By': 'COMMON.USER.TYPE.REVIEWED',
    User: 'COMMON.USER.SINGLE',
  };

  count: number = 0;
  filter: Filter = new Filter();
  isFilterWindowOpened: boolean = false;
  isDynamicFilterWindowOpened: boolean = false;
  checklistItems: ChecklistItem[] = [];

  tableListPage: TableListPage = new TableListPage();
  emcOptions: EmcOptions[] = [];
  cellTooltip = { visible: false, x: 0, y: 0, text: '' };

  constructor(
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private substationTemplateService: SubstationTemplateService,
    private taggedSubstationService: TaggedSubstationService,
    private router: Router,
    private emcService: EntityModelCustomizationService,
    private utilitySharedService: UtilitySharedService,
    public permissionsService: PermissionsService
  ) {
    this.utilityId = this.utilitySharedService.getUtilityId() ?? 0;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.substationTemplateId = Number(params.get('id'));
      this.filter = new Filter(this.utilityId, this.substationTemplateId);

      const cachedFilters = localStorage.getItem(
        `filterDynamicSubstations|${this.substationTemplateId}`,
      );
      if (cachedFilters) {
        const parsed = JSON.parse(cachedFilters);
        this.filter = parsed;
        this.filter.substationTemplateId = this.substationTemplateId;
        this.filter.filterParams.statuses = parsed.filterParams.statuses ?? [];

        this.createFilterCards();
        localStorage.removeItem(
          `filterDynamicSubstations|${this.substationTemplateId}`,
        );
      }

      var utilityId = Number(this.utilitySharedService.getUtilityId());

      this.getCardEmcOptions(utilityId);
      this.onPageChange(this.filter.pageInfo.page);
      this.getSidebarSubstations();
    });
  }

  private getCardEmcOptions(utilityId: number) {
    this.emcService
      .getSelectedEmcOptions(
        SectionDefinitionEntityType.SubstationTemplate,
        Purpose.Card,
        utilityId,
        this.substationTemplateId,
      )
      .subscribe({
        next: (response: any) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.emcOptions = responseData.data ?? [];
          } else {
            this.toastr.error(
              responseData?.message || 'Failed to fetch EMC options',
            );
          }
        },
        error: (error) => {
          this.toastr.error('Error occurred while fetching EMC options');
        },
      });
  }

  getSidebarSubstations() {
    this.substationTemplateService.getAllForSidebar(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        this.items = responseData ? responseData.data : [];
        this.title =
          this.items?.find((item: any) => item.id === this.substationTemplateId)
            ?.className || '';
      },
      error: (_) => {
        this.toastr.error('Error occurred while loading substation templates');
      },
    });
  }

  onPageChange(currentPage: number) {
    this.filter.pageInfo.page = currentPage;
    this.reloadTable(currentPage);
  }

  reloadTable(page: number | null = null) {
    if (page) this.filter.pageInfo.page = page;
    this.getAllDynamic(this.filter);
  }

  private getAllDynamic(filter: Filter) {
    this.taggedSubstationService.getAll(filter).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        this.tableListPage = responseData?.data ?? new TableListPage();
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  onRowClick(guid: string) {
    localStorage.setItem(
      `filterDynamicSubstations|${this.substationTemplateId}`,
      JSON.stringify(this.filter),
    );
    this.router.navigate(['edit', guid], { relativeTo: this.route });
  }

  onDynamicFilterWindowChange(val: boolean) {
    this.isDynamicFilterWindowOpened = val;
  }

  closeDynamicFilterWindow() {
    this.isDynamicFilterWindowOpened = false;
  }

  onFilterWindowChange(val: boolean) {
    this.isFilterWindowOpened = val;
  }

  closeFilterWindow() {
    this.isFilterWindowOpened = false;
  }

  onSortTypeChange(option: SortType) {
    this.filter.filterParams.sortBy = option;
    this.filter.filterParams.sortByName = this.mapSortBy[option];
    this.reloadTable();
  }

  onAddFilterCard(data: ChecklistItem) {
    this.filter.searchObject.searchValue = data.selectedValue;

    this.createFilterCards();
  }

  onAddFilter() {
    this.createFilterCards();
  }

  applyFilter() {
    this.reloadTable(1);
    this.dynamicCards.refresh();
  }

  removeFilter(checklistItem: ChecklistItem) {
    if (checklistItem.property === 'Date Type') {
      return;
    }

    const filter = this.checklistItems.find(
      (f) =>
        f.property === checklistItem.property &&
        f.selectedValue === checklistItem.selectedValue,
    );

    if (filter) {
      this.checklistItems = this.checklistItems.filter((f) => f !== filter);

      this.updateFilterFromRemovedItem(filter);

      this.createFilterCards();
      if (this.checklistItems.length === 0) {
        this.applyFilter();
      }
    }
  }
  searchfilter(obj: any) {
    this.filter.searchObject = obj;
  }
  resetFilter() {
    this.filter = new Filter(this.utilityId, this.substationTemplateId);
    this.checklistItems = [];
    this.onPageChange(1);
  }

  private updateFilterFromRemovedItem(item: ChecklistItem) {
    const staticFields = this.filter.filterParams;

    switch (item.property) {
      case 'Search':
        this.filter.searchObject = new EmcSearchOutput();
        break;

      case 'Validation Status':
        staticFields.statuses = staticFields.statuses.filter(
          (s) => (this.statusLabelMap[s] ?? s.toString()) !== item.selectedValue,
        );
        break;

      case 'Review Status':
        staticFields.reviewStatus = null;
        break;

      case 'Audit Status':
        staticFields.auditStatus = null;
        break;

      case 'GIS Review Status':
        staticFields.gisReviewStatus = null;
        break;

      case 'GIS Update Status':
        staticFields.gisUpdateStatus = null;
        break;

      case 'Type':
        staticFields.type = null;
        break;

      case 'Area':
        staticFields.selectedAreaModels =
          staticFields.selectedAreaModels.filter(
            (a) => a.name !== item.selectedValue,
          );
        break;

      case 'First Captured User':
      case 'Last Captured User':
      case 'Reassigned By':
      case 'Validated By':
      case 'Audited By':
      case 'Reviewed By':
      case 'User':
        staticFields.selectedUserModels =
          staticFields.selectedUserModels.filter(
            (u) => u.name !== item.selectedValue,
          );
        break;

      case 'Transmission Station':
        staticFields.selectedTsModels = staticFields.selectedTsModels.filter(
          (ts) => ts.name !== item.selectedValue,
        );
        break;

      case 'Date From':
        staticFields.dateFrom = null;
        break;

      case 'Date To':
        staticFields.dateTo = null;
        break;

      case 'Date Type':
        break;

      default:
        const dfIndex = this.filter.dynamicFilters.findIndex((d) =>
          item.property.startsWith(d.attributeLabel),
        );

        if (dfIndex !== -1) {
          const df = this.filter.dynamicFilters[dfIndex];

          if (item.property.endsWith('From')) {
            df.from = null;
          } else if (item.property.endsWith('To')) {
            df.to = null;
          } else {
            if (df.selectedOptions && df.optionDefinitions) {
              const option = df.optionDefinitions.find(
                (opt) => opt.optionValue === item.selectedValue,
              );

              if (option) {
                const idToRemove = option.optionId;
                df.selectedOptions = df.selectedOptions.filter(
                  (optId) => optId !== idToRemove,
                );
              }
            }

            df.optionDefinitions = df.optionDefinitions?.filter(
              (opt) => opt.optionValue !== item.selectedValue,
            );
          }

          const hasOptions =
            df.selectedOptions && df.selectedOptions.length > 0;
          const hasOptionDefinitions =
            df.optionDefinitions && df.optionDefinitions.length > 0;
          const hasFromTo = df.from != null || df.to != null;

          if (!hasOptions && !hasFromTo && !hasOptionDefinitions) {
            this.filter.dynamicFilters.splice(dfIndex, 1);
          }
        }
        break;
    }
  }

  download() {
    this.taggedSubstationService.downloadDynamic(this.filter).subscribe({
      next: (response) => {
        const data = response;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  private createFilterCards() {
    const items: ChecklistItem[] = [];

    const staticFields = this.filter.filterParams;

    if (this.filter.searchObject.searchValue) {
      items.push(
        new ChecklistItem(
          'Search',
          this.filter.searchObject.searchValue,
          this.mapFilterLabels['Search'],
          this.mapFilterValues[staticFields.search],
        ),
      );
    }

    staticFields.statuses.forEach((status) => {
      const statusLabel = this.statusLabelMap[status] ?? status.toString();
      items.push(
        new ChecklistItem(
          'Validation Status',
          statusLabel,
          this.mapFilterLabels['Validation Status'],
          this.mapFilterValues[statusLabel] ?? statusLabel,
        ),
      );
    });

    if (staticFields.reviewStatus != null) {
      const reviewLabel = staticFields.reviewStatus === 1 ? 'Reviewed' : 'Not Reviewed';
      items.push(new ChecklistItem('Review Status', reviewLabel, this.mapFilterLabels['Review Status'], reviewLabel));
    }

    if (staticFields.auditStatus != null) {
      const auditLabel = staticFields.auditStatus === 1 ? 'Audited' : 'Not Audited';
      items.push(new ChecklistItem('Audit Status', auditLabel, this.mapFilterLabels['Audit Status'], auditLabel));
    }

    if (staticFields.gisReviewStatus != null) {
      const gisReviewLabel = this.gisReviewStatusLabelMap[staticFields.gisReviewStatus] ?? '';
      items.push(new ChecklistItem('GIS Review Status', gisReviewLabel, this.mapFilterLabels['GIS Review Status'], gisReviewLabel));
    }

    if (staticFields.gisUpdateStatus != null) {
      const gisUpdateLabel = this.gisUpdateStatusLabelMap[staticFields.gisUpdateStatus] ?? '';
      items.push(new ChecklistItem('GIS Update Status', gisUpdateLabel, this.mapFilterLabels['GIS Update Status'], gisUpdateLabel));
    }

    if (staticFields.type != null && staticFields.type !== 0) {
      const typeValue = staticFields.type.toString();
      items.push(
        new ChecklistItem(
          'Type',
          typeValue,
          this.mapFilterLabels['Type'],
          this.mapFilterValues[typeValue],
        ),
      );
    }

    staticFields.selectedAreaModels.forEach((area) => {
      const name = area.name;
      items.push(
        new ChecklistItem(
          'Area',
          name,
          this.mapFilterLabels['Area'],
          this.mapFilterValues[name],
        ),
      );
    });

    const userTypeDisplayMap: Record<number, string> = {
      1: 'First Captured User',
      2: 'Last Captured User',
      3: 'Reassigned By',
      4: 'Validated By',
      5: 'Audited By',
      6: 'Reviewed By',
    };
    const userTypeLabel = staticFields.userType != null && (staticFields.userType as number) !== 0
      ? (userTypeDisplayMap[staticFields.userType as number] ?? 'User')
      : 'User';

    staticFields.selectedUserModels.forEach((user) => {
      items.push(
        new ChecklistItem(
          userTypeLabel,
          user.name,
          this.mapFilterLabels[userTypeLabel] ?? this.mapFilterLabels['User'],
          user.name,
        ),
      );
    });

    staticFields.selectedTsModels.forEach((ts) => {
      const name = ts.name;
      items.push(
        new ChecklistItem(
          'Transmission Station',
          name,
          this.mapFilterLabels['Transmission Station'],
          this.mapFilterValues[name],
        ),
      );
    });

    if (staticFields.dateFrom) {
      items.push(
        new ChecklistItem(
          'Date From',
          staticFields.dateFrom,
          this.mapFilterLabels['Date From'],
          this.mapFilterValues[staticFields.dateFrom],
        ),
      );
    }

    if (staticFields.dateTo) {
      items.push(
        new ChecklistItem(
          'Date To',
          staticFields.dateTo,
          this.mapFilterLabels['Date To'],
          this.mapFilterValues[staticFields.dateTo],
        ),
      );
    }

    if (
      (staticFields.dateFrom || staticFields.dateTo) &&
      staticFields.dateType
    ) {
      items.push(
        new ChecklistItem(
          'Date Type',
          this.filter.filterParams.dateTypeString,
          this.mapFilterLabels['Date Type'],
          this.mapFilterValues[this.filter.filterParams.dateTypeString],
        ),
      );
    }

    this.filter.dynamicFilters.forEach((df) => {
      df.optionDefinitions?.forEach((option) => {
        items.push(
          new ChecklistItem(
            option.attributeLabel,
            option.optionValue,
            this.mapFilterLabels[option.attributeLabel],
            this.mapFilterValues[option.optionValue],
          ),
        );
      });

      if (df.from) {
        const formatted = df.from.toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        });
        items.push(
          new ChecklistItem(
            df.attributeLabel + ' From',
            formatted,
            this.mapFilterLabels[df.attributeLabel + ' From'],
            this.mapFilterValues[formatted],
          ),
        );
      }

      if (df.to) {
        const formatted = df.to.toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        });
        items.push(
          new ChecklistItem(
            df.attributeLabel + ' To',
            formatted,
            this.mapFilterLabels[df.attributeLabel + ' To'],
            this.mapFilterValues[formatted],
          ),
        );
      }
    });

    this.checklistItems = items;
  }

  showCellTooltip(event: MouseEvent) {
    const td = event.currentTarget as HTMLElement;
    if (td.scrollWidth <= td.clientWidth && td.scrollHeight <= td.clientHeight) return;
    const text = td.textContent?.trim() || '';
    if (!text) return;
    const rect = td.getBoundingClientRect();
    this.cellTooltip = {
      visible: true,
      x: rect.left,
      y: rect.bottom + 4,
      text,
    };
  }

  hideCellTooltip() {
    this.cellTooltip.visible = false;
  }
}
